import { useMemo, useState } from 'react';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import NodeFilterSelect from './NodeFilterSelect';
import NodeFilterMultiSelect from './NodeFilterMultiSelect';
import { ALL_VALUE, MAX_NODE_FILTERS } from './nodeFilterUtils';

/**
 * Панель фильтров по результатам анализа.
 *
 * У пакета electrobike тринадцать фильтров, и в раскрытом виде они занимали экран целиком:
 * до первого графика приходилось скроллить, а сами графики — то, зачем страницу открывают.
 * Поэтому панель свёрнута, пока фильтров больше порога, а в свёрнутом виде показывает,
 * сколько условий выбрано.
 *
 * Внутри фильтры разведены на две группы. Шесть фильтров «провален пункт» — это не то же
 * самое, что фильтры по классификации: они отбирают звонки с проваленным пунктом чек-листа
 * и нужны заметно реже, поэтому лежат отдельной подгруппой.
 */
const COLLAPSE_THRESHOLD = 6;
const CHECKLIST_KIND = 'checklist_results';
const GRID_CLASS = 'grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4';

const countSelected = (values, filters) =>
  filters.reduce((count, filter) => {
    const value = values[filter.id];

    if (Array.isArray(value)) {
      return count + value.length;
    }

    return count + (value && value !== ALL_VALUE ? 1 : 0);
  }, 0);

const FilterGrid = ({ filters, values, onChange }) => (
  <div className={GRID_CLASS}>
    {filters.map((filter) => {
      const FilterComponent = filter.control === 'multi_select' ? NodeFilterMultiSelect : NodeFilterSelect;

      return (
        <FilterComponent
          key={filter.id}
          filter={filter}
          value={values[filter.id] ?? (filter.control === 'multi_select' ? [] : ALL_VALUE)}
          onChange={(value) => onChange(filter.id, value)}
        />
      );
    })}
  </div>
);

const NodeFiltersPanel = ({ filters, values, onChange, onReset, packName, packVersion, entryCount }) => {
  const { classification, checklists } = useMemo(
    () => ({
      classification: filters.filter((filter) => filter.kind !== CHECKLIST_KIND),
      checklists: filters.filter((filter) => filter.kind === CHECKLIST_KIND)
    }),
    [filters]
  );

  const selectedCount = countSelected(values, filters);
  const [isOpen, setIsOpen] = useState(filters.length <= COLLAPSE_THRESHOLD);
  const [showChecklists, setShowChecklists] = useState(false);

  return (
    // `data-node-filters` — опора e2e: у панели нет заголовка-роли, а её поля подписаны
    // «Все…» так же, как соседние фильтры раздела.
    <div data-node-filters className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <button
          type="button"
          data-ev="nodes.filters.toggle"
          onClick={() => setIsOpen((current) => !current)}
          className="flex items-center gap-2 text-sm font-semibold hover:text-primary"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Фильтры по результатам анализа
          {selectedCount > 0 && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              выбрано: {selectedCount}
            </span>
          )}
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <div className="flex items-center gap-3">
          {selectedCount > 0 && (
            <button
              type="button"
              data-ev="nodes.filters.reset"
              onClick={onReset}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            >
              Сбросить все
            </button>
          )}
          <span className="text-xs text-muted-foreground">
            {/* Именно «активная»: графики считаются по всем версиям, а эта — та,
                которой анализируются новые звонки. */}
            Пакет «{packName}», активная версия {packVersion}
          </span>
        </div>
      </div>

      {isOpen && (
        <div className="mt-3 space-y-3">
          <FilterGrid filters={classification} values={values} onChange={onChange} />

          {checklists.length > 0 && (
            <div className="rounded-lg border border-dashed p-3">
              <button
                type="button"
                data-ev="nodes.filters.toggle_checklists"
                onClick={() => setShowChecklists((current) => !current)}
                className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-primary"
              >
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showChecklists ? 'rotate-180' : ''}`} />
                Провалы по чек-листам ({checklists.length})
              </button>

              {showChecklists && (
                <div className="mt-3">
                  <FilterGrid filters={checklists} values={values} onChange={onChange} />
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Показаны звонки, обработанные этой версией пакета. Несколько выбранных значений
            одного фильтра означают «выполнены все условия сразу».
          </p>

          {entryCount > MAX_NODE_FILTERS && (
            <p className="text-xs text-amber-700">
              Выбрано условий: {entryCount}. Учитываются первые {MAX_NODE_FILTERS} —
              снимите лишние значения, иначе цифры на графиках не соответствуют фильтру.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default NodeFiltersPanel;
